import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, ShieldAlert, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../App';
import PageHeader from '../components/PageHeader';
import AccountProtection from './AccountProtection';
import './AccountProtectionOverview.css';

const TABS = [
  ['signals', 'Mehrfachkonto-Hinweise'],
  ['automatic', 'Automatische Geräteregeln'],
  ['review', 'Zu prüfen'],
  ['confirmed', 'Bestätigt'],
  ['allowed', 'Freigegeben'],
  ['dismissed', 'Korrigiert'],
  ['referral', 'Bonus abgelehnt'],
];
const STATE = { review: 'Zu prüfen', confirmed: 'Bestätigt', allowed: 'Freigegeben', dismissed: 'Korrigiert' };
const PROOF = { verified_same_owner: 'Gleiche Inhaberschaft belegt', support_review: 'Supportprüfung' };
const REFERRAL = {
  device_already_rewarded: 'Gerät hat bereits einen Referral-Bonus erhalten',
  device_missing: 'Gerät konnte nicht bestätigt werden',
  account_not_eligible: 'Konto ist nicht bonusberechtigt',
};
const euro = cents => `${(Number(cents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const when = value => value ? new Date(value).toLocaleString('de-DE') : '–';
const shortId = value => value ? `${String(value).slice(0, 8)}…` : '–';

export default function AccountProtectionOverview() {
  const { can } = useAuth();
  const permitted = can('account_protection.view') || can('account_protection.manage');
  const [tab, setTab] = useState('signals');
  const [days, setDays] = useState(90);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    if (!permitted) return;
    const requestId=++requestSequence.current;
    setLoading(true); setError('');
    try {
      const result = await api.get(`/admin/account-protection/cases?kind=${tab}&page=${page}&limit=50&days=${days}`);
      if(requestId===requestSequence.current)setData(result.data);
    } catch {
      if(requestId===requestSequence.current){setData(null);setError('Kontoschutz-Übersicht konnte nicht geladen werden.');}
    } finally { if(requestId===requestSequence.current)setLoading(false); }
  }, [days, page, permitted, tab]);

  useEffect(() => { load(); return()=>{requestSequence.current++;}; }, [load]);
  if (!permitted) return null;
  const switchTab = next => { setTab(next); setPage(0); setSelected(null); };

  return <div className="account-protection-overview">
    <PageHeader title="Kontoschutz" description="Mehrfachkonten prüfen und bestätigte Schutzentscheidungen nachvollziehen."
      actions={<button type="button" className="apo-refresh" onClick={load} disabled={loading}><RefreshCw size={16} /> Aktualisieren</button>} />

    <div className="apo-contract" data-testid="account-protection-contract">
      <ShieldAlert size={20} />
      <div><strong>Ein gemeinsames Gerät ist ein Prüfhinweis, kein Personenbeweis.</strong>
        <span> Die Geräteregel kann weitere Konten automatisch bei Bargeld und neuen Vorteilen einschränken. Eine vollständig bezahlte Kartenfahrt hebt nur die reine Gerätesperre für Bargeld auf. Gleiche Inhaberschaft braucht einen eigenen Nachweis.</span></div>
    </div>

    <div className="apo-tabs" role="tablist" aria-label="Kontoschutz-Fallarten">
      {TABS.map(([key, label]) => <button type="button" role="tab" aria-selected={tab === key} key={key}
        onClick={() => switchTab(key)}>{label}<b>{data?.counts?.[key] ?? '–'}</b></button>)}
    </div>

    {tab === 'signals' && <label className="apo-period">Zeitraum
      <select aria-label="Zeitraum" value={days} onChange={event => { setDays(Number(event.target.value)); setPage(0); }}>
        <option value={30}>30 Tage</option><option value={90}>90 Tage</option><option value={180}>180 Tage</option><option value={365}>365 Tage</option>
      </select>
    </label>}
    {error && <p className="apo-error" role="alert">{error}</p>}
    {loading && <p className="apo-empty">Lade Kontoschutzfälle …</p>}
    {!loading && !error && data?.rows?.length === 0 && <p className="apo-empty">Keine Einträge in dieser Ansicht.</p>}

    {!loading && data?.rows?.length > 0 && <div className="apo-table-wrap"><table className="apo-table">
      <thead><tr>{tab === 'signals' ? <>
        <th>Gerätegruppe / Kunde</th><th>Nutzung</th><th>Storno / No-show</th><th>Vorteile</th><th>Schutzstand</th>
      </> : <><th>Kunde</th><th>Einordnung</th><th>Zeit / Gültigkeit</th><th>Prüfstand</th></>}<th>Aktion</th></tr></thead>
      <tbody>{data.rows.map(row => <tr key={`${row.kind}-${row.device_group || ''}-${row.customer_id}`}>
        {tab === 'signals' ? <SignalCells row={row} /> : <CaseCells row={row} />}
        <td><button type="button" className="apo-open" onClick={event => setSelected({ id: row.customer_id, name: row.customer_name, trigger: event.currentTarget })}>Kundenprüfung öffnen</button></td>
      </tr>)}</tbody>
    </table></div>}

    {data && <div className="apo-pagination"><span>{data.total} Einträge</span><div>
      <button type="button" disabled={page === 0 || loading} onClick={() => setPage(p => p - 1)}>Zurück</button>
      <span>Seite {page + 1}</span>
      <button type="button" disabled={!data.has_more || loading} onClick={() => setPage(p => p + 1)}>Weiter</button>
    </div></div>}

    {selected && <CustomerReview key={selected.id} customer={selected} onClose={() => setSelected(null)} />}
  </div>;
}

function CustomerReview({ customer, onClose }) {
  const backdrop = useRef(null), panel = useRef(null), closer = useRef(null);
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    const overflow = document.body.style.overflow;
    const background = [...document.body.children].filter(node => node !== backdrop.current).map(node => [node, node.inert]);
    background.forEach(([node]) => { node.inert = true; });
    document.body.style.overflow = 'hidden';
    closer.current?.focus({ preventScroll: true });
    const onKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close.current(); return; }
      if (event.key !== 'Tab') return;
      const nodes = [...panel.current.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex="0"]')].filter(node => {
        for (let ancestor = node.parentElement; ancestor && ancestor !== panel.current; ancestor = ancestor.parentElement) {
          if (ancestor.tagName === 'DETAILS' && !ancestor.open
            && !(node.tagName === 'SUMMARY' && node.parentElement === ancestor)) return false;
        }
        return true;
      });
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panel.current.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !panel.current.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    };
    const keepFocus = event => { if (!panel.current?.contains(event.target)) closer.current?.focus(); };
    document.addEventListener('keydown', onKey); document.addEventListener('focusin', keepFocus);
    return () => {
      document.removeEventListener('keydown', onKey); document.removeEventListener('focusin', keepFocus);
      document.body.style.overflow = overflow;
      background.forEach(([node, inert]) => { node.inert = inert; });
      if (customer.trigger?.isConnected) customer.trigger.focus({ preventScroll: true });
    };
  }, [customer.trigger]);
  return createPortal(<div ref={backdrop} className="apo-dialog-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) close.current(); }}>
    <section ref={panel} className="apo-detail" role="dialog" aria-modal="true" aria-labelledby="apo-review-title" tabIndex={-1}>
      <header className="apo-detail-head"><div><h2 id="apo-review-title">Kundenprüfung · {customer.name || shortId(customer.id)}</h2><p>Kundenkonto: {customer.id}</p></div>
        <button ref={closer} type="button" aria-label="Kundenprüfung schließen" onClick={() => close.current()}><X size={18} /> Schließen</button></header>
      <div className="apo-detail-body"><AccountProtection userId={customer.id} /></div>
    </section>
  </div>, document.body);
}

function SignalCells({ row }) {
  return <>
    <td><strong>{row.device_group}</strong><span>{row.customer_name} · {shortId(row.customer_id)}</span><small>{row.device_accounts} Konten · {row.device_rides} Gerätefahrten</small></td>
    <td><strong>{row.account_rides} Fahrten</strong><span>Registriert: {when(row.registered_at)}</span></td>
    <td><strong>{row.customer_cancellations} Storno · {row.no_shows} No-show</strong><span>{row.open_fee_count} offene Stornogebühr(en) · {row.open_shortfall_count} Fahrpreisrest(e)</span><small>{euro(row.open_debt_cents)} offen · {row.fee_waivers} Erlassbuchung(en)</small></td>
    <td><strong>{row.benefit_rides} Fahrt(en) mit Rabatt/Guthaben</strong><span>{row.referral_denials} abgelehnte Referral-Boni</span></td>
    <td><Status value={row.protection_state} active={row.protection_active} proof={row.protection_proof} /></td>
  </>;
}

function CaseCells({ row }) {
  if(row.kind==='automatic')return <>
    <td><strong>{row.customer_name}</strong><span>{shortId(row.customer_id)}</span></td>
    <td><strong>Weiteres Konto auf derselben Installation</strong><span>{row.mode==='enforce'?'Regel aktiv':row.mode==='observe'?'Nur Beobachtung':'Regel ausgeschaltet'}</span></td>
    <td>{when(row.detected_at)}</td><td>
      <span>{row.cash_blocked?'Bargeld gesperrt':row.cash_release==='paid_card_ride'?'Bargeld-Gerätesperre nach Kartenfahrt aufgehoben':row.cash_release==='support_release'?'Bargeld-Gerätesperre durch Support aufgehoben':'Keine Bargeld-Gerätesperre'}</span>
      <small>{row.promo_blocked?'Neue Vorteile gesperrt':'Keine Vorteilssperre durch diese Regel'} · andere Schutzregeln bleiben wirksam</small>
    </td>
  </>;
  if (row.kind === 'referral') return <>
    <td><strong>{row.customer_name}</strong><span>{shortId(row.customer_id)}</span></td>
    <td><strong>Bonus abgelehnt</strong><span>{REFERRAL[row.reason] || row.reason}</span></td>
    <td>{when(row.created_at)}</td><td><span className="apo-pill is-neutral">Kein Kontourteil</span></td>
  </>;
  return <>
    <td><strong>{row.customer_name}</strong><span>{shortId(row.customer_id)}</span></td>
    <td><strong>{STATE[row.state] || row.state}</strong><span>{PROOF[row.proof] || row.proof}</span></td>
    <td><span>Entscheidung: {row.occurred_at_ms ? when(row.occurred_at_ms) : '–'}</span><small>Prüffrist: {when(row.expires_at)}</small></td>
    <td><Status value={row.state} active={row.active} proof={row.proof} /></td>
  </>;
}

function Status({ value, active, proof }) {
  if (!value) return <span className="apo-pill is-neutral">Nur Hinweis</span>;
  return <><span className={`apo-pill is-${value}`}>{STATE[value] || value}{active === false ? ' · abgelaufen' : ''}</span>
    {proof ? <small>{PROOF[proof] || proof}</small> : null}</>;
}
